import { useEffect, useReducer, useState } from "react"
import PropTypes from "prop-types"
import uniqBy from "lodash/uniqBy"
import isEmpty from "lodash/isEmpty"
import moment from "moment"

import BackgroundColorContext from "./context"

import ServiceSelector from "./service_selector"
import StepSelector from "./step_selector"
import ServiceConnection from "./service_connection"
import Preview from "./preview"
import FilterCode from "./filter_code"
import { previousStep, steps, stepsTitle } from "./steps"
import { formatStep, initialState, reducer } from "./reducer"
import Header from "./header"
import InitialStep from "./initial_step"
import StepFieldsSelector from "./step_fields_selector"
import {
  DIYLoadServiceActionsAndFieldsQuery,
  DIYLoadServiceQueriesAndFieldsQuery,
  DIYLoadServiceTriggersAndFieldsQuery,
} from "./queries"
import AppletLimitWarning from "./applet_limit_warning"
import MakeItYourOwnModal from "./make_it_your_own_modal"
import UpgradeToProModal from "../upgrade_to_pro_modal"

import colors from "foundation/_colors_export.scss?variables"

import "./index.scss"
import {
  loadActionDefaultValues,
  loadPreview,
  loadPrivateServices,
} from "./api"
import { ingredientsMetadataFromState } from "./utils/ingredients_metadata_from_state"
import { selectedActionsAboveLimit } from "./utils/selected_actions_above_limit"
import {
  trackClickEditAppletSettings,
  trackStateChangeServiceConnected,
  trackSystemServiceConnectionSkipped,
  trackSystemStepConfigSkipped,
} from "./utils/telemetry"
import { pluralizeStepType } from "shared/lib/utils"

import ErrorBoundary from "shared/components/error_boundary"

const isUserConnected = service => service.live_channels.some(lc => !lc.offline)

const DIY = ({
  importedState,
  filterCodeTemplate,
  preloadedServices,
  urls,
  msa,
  serviceSuggestion,
  personalAppletsCount,
  proPlusProductId,
  user: propsUser,
}) => {
  const [makeItYourOwn, updateMakeItYourOwn] = useState({
    showModal: false,
  })
  const [upgradeToPro, updateUpgradeToPro] = useState({
    showModal: false,
    step: "queries",
  })

  const [state, dispatch] = useReducer(reducer, {
    ...initialState({ user: propsUser }),
    loading: !!importedState,
    editing: !!importedState,
  })

  const user = state.user

  const userOwned =
    importedState?.normalized_applet?.author_id === window.App.userId

  const publishedByCurrentUser =
    userOwned && importedState?.normalized_applet?.published === true

  const publishedAppletData = {
    publishedAt: moment
      .utc(
        new Date(importedState?.normalized_applet?.created_at?.substring(0, 10))
      )
      .format("LL"),
    enabledCount: importedState?.normalized_applet?.installs_count,
  }

  const userAtQuota =
    user.appletQuota.total > 0 && user.appletQuota.remaining === 0

  useEffect(() => {
    if (!state.editing && userAtQuota) {
      updateUpgradeToPro({
        showModal: true,
        step: "applet_quota",
      })
    }
  }, [state.editing, userAtQuota])

  useEffect(() => {
    const effect = async () => {
      const privateServices = await loadPrivateServices()
      const allServices = privateServices.concat(preloadedServices)
      const suggestedServices = serviceSuggestion
        ? allServices.filter(s => s.module_name === serviceSuggestion)
        : []
      onServicesLoaded(
        uniqBy(suggestedServices.concat(allServices), s => s.module_name)
      )
      dispatch({ type: "LOADING", value: false })
    }
    effect()
  }, [])

  const onServicesLoaded = services => {
    dispatch({
      type: "SERVICES_LOADED",
      value: {
        servicesWithTriggers: services.filter(
          service => service.public_triggers.length > 0
        ),
        servicesWithQueries: services.filter(
          service => service.public_queries.length > 0
        ),
        servicesWithActions: services.filter(
          service => service.public_actions.length > 0
        ),
      },
    })

    if (importedState) {
      const {
        id,
        normalized_applet,
        applet_trigger,
        applet_actions,
        applet_queries,
      } = importedState

      const trigger = applet_trigger.trigger
      const actions = applet_actions.map(a => a.action)
      const queries = applet_queries.map(q => q.query)

      const formatStepForOverride = (step, type) => {
        let owner = `/${pluralizeStepType(type)}/${step.full_module_name}`
        if (type === "action") {
          const index = actions
            .filter(a => a.full_module_name === step.full_module_name)
            .indexOf(step)
          owner += `/${index}`
        }

        let fields = {}
        const storedFields = normalized_applet.stored_fields.filter(f => {
          return f.owner === owner
        })

        const liveChannelField = storedFields.find(
          sf => sf.name === "_live_channel_id"
        )
        if (liveChannelField) {
          fields["_live_channel_id"] = liveChannelField
        }

        fields = step[`${type}_fields`].reduce((memo, f, order) => {
          const storedField = storedFields.find(sf => sf.name === f.name)
          let currentValue = storedField.value
          if (storedField.is_hidden || storedField.value === null) {
            currentValue = storedField.default_value
          }

          memo[f.name] = {
            ...f,
            order,
            hidden: storedField.is_hidden,
            owner: storedField.owner,
            value: currentValue,
          }
          return memo
        }, fields)

        const service = services.find(s => s.id === step.channel.id)
        service.allow_multiple_live_channels =
          step.channel.allow_multiple_live_channels
        service.live_channels = step.channel.live_channels

        step.live_channel_id = normalized_applet.permissions.find(
          p => p.id === owner
        ).live_channel.id

        return formatStep({ step, fields }, type, service)
      }

      trackClickEditAppletSettings(id)

      dispatch({
        type: "SET_OVERRIDE_STATE",
        value: {
          appletId: id,
          name: normalized_applet.name,
          pushEnabled: normalized_applet.push_enabled,
          selectedTrigger: formatStepForOverride(trigger, "trigger"),
          selectedQueries: queries.map(q => formatStepForOverride(q, "query")),
          selectedActions: actions.map(a => formatStepForOverride(a, "action")),
          filterCode: importedState.filter_code,
        },
      })
    }
  }

  // Check every time that trigger and actions changed.
  // Load preview mutation only in case that we have 1 trigger and 1 actions. Otherwise, use trigger context.
  useEffect(() => {
    if (state.selectedTrigger !== null && state.selectedActions.length > 0) {
      const hasQueries = state.selectedQueries.length > 0
      const isMultipleAction = state.selectedActions.length > 1

      const updatePreviewWithTrigger = () =>
        updatePreview({
          name: "",
          serviceSlug: state.selectedTrigger.service.module_name,
          brandColor: state.selectedTrigger.service.brand_color,
          pushEnabled: false,
        })

      const loadPreviewMutation = async () => {
        try {
          const response = await loadPreview({
            trigger: state.selectedTrigger.full_module_name,
            triggerFields: state.selectedTrigger.fields,
            triggerLiveChannelId: state.selectedTrigger.live_channel_id,
            action: state.selectedActions[0].full_module_name,
            actionFields: state.selectedActions[0].fields,
            actionLiveChannelId: state.selectedActions[0].live_channel_id,
          })

          updatePreview({
            name: response.statementPreview.normalized_applet.name || "",
            brandColor: response.statementPreview.normalized_applet.brand_color,
            serviceSlug:
              response.statementPreview.normalized_applet.service_slug,
            pushEnabled: response.statementPreview.push_enabled,
          })
        } catch (error) {
          updatePreviewWithTrigger()
        }
      }

      hasQueries || isMultipleAction
        ? updatePreviewWithTrigger()
        : loadPreviewMutation()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.selectedTrigger,
    state.selectedActions.length,
    state.selectedQueries.length,
  ]) // length is necessary for actions because when removes one action, useEffect isn't called.

  const goBack = () => {
    dispatch({
      type: "CHANGE_STEP",
      value: previousStep(state),
    })
  }

  useEffect(() => {
    window.addEventListener("popstate", handlePopState)

    return function cleanup() {
      window.removeEventListener("popstate", handlePopState)
    }
  }, []) // Register listeners once

  const handlePopState = event => {
    if (state.step !== steps.INITIAL) {
      event.preventDefault()
      goBack()
    }
  }

  useEffect(() => {
    if (state.dataChanged) {
      window.addEventListener("beforeunload", handleBeforeUnload, {
        once: true,
      })

      // cleanup will be calling in the next re-render execution. It doesn't happen when user finish DIY creation.
      // It's necessary to call manually to remove event listener when redirect user to applet page (using location.assign)
      return cleanupBeforeUnload
    }
  })

  const handleBeforeUnload = event => {
    dispatch({ type: "LOADING", value: false })

    event.preventDefault() // as yet unused standard
    event.returnValue =
      "You have unsaved changes. Do you want to leave this page and discard your changes?"
  }

  const cleanupBeforeUnload = function cleanup() {
    window.removeEventListener("beforeunload", handleBeforeUnload)
  }

  const makeItYourOwnFeaturePrompt = async () => {
    const communityApplet =
      state.editing && !(userOwned || state.makingItTheirOwn)

    if (!communityApplet) {
      return Promise.resolve()
    }

    const showUpgradeModal = step => {
      updateUpgradeToPro({
        showModal: true,
        step,
      })
      return Promise.reject("User needs upgrade")
    }

    if (
      !["legacy_pro", "pro"].includes(user.tier) &&
      importedState?.normalized_applet?.pro_features
    ) {
      await showUpgradeModal("convert_pro_plus_applet")
    } else if (
      importedState?.normalized_applet?.intermediate_pro_features &&
      user.tier === "free"
    ) {
      await showUpgradeModal("multi_action")
    } else if (
      !["legacy_pro", "pro"].includes(user.tier) &&
      personalAppletsCount >= user.appletQuota.total
    ) {
      await showUpgradeModal("applet_quota")
    }

    return new Promise((resolve, reject) => {
      updateMakeItYourOwn({
        showModal: true,
        onConvert: () => {
          dispatch({
            type: "MAKING_IT_THEIR_OWN",
          })
          window.appendFlash("Converted to your own Applet", "success", resolve)
        },
        onCancel: () => reject("cancellation"),
      })
    })
  }

  const proFeatureGate = async permissionName => {
    if (user.permissions[permissionName]?.permitted) {
      return Promise.resolve()
    } else {
      updateUpgradeToPro({
        showModal: true,
        step: permissionName,
      })
      return Promise.reject("cancellation")
    }
  }

  const updatePreview = value => {
    dispatch({
      type: "UPDATE_PREVIEW",
      value,
    })
  }

  const updateLoading = value => dispatch({ type: "LOADING", value })

  const selectingTandaFieldsProps = () => ({
    step: state.selectedStep,
    sourceAppletId: state.appletId,
    stepErrors: state.errors,
    editMode: state.editing,
    addingStep: state.addingStep,
    userOwned,
    userAllowMultipleLiveChannels:
      user.permissions.multi_service_account.permitted === true,
    makeItYourOwnFeaturePrompt,
    makingItTheirOwn: state.makingItTheirOwn,
    connectionFinishedUrl: urls.connectionFinishedUrl,
    changeServiceAccount: live_channel_id =>
      dispatch({ type: "CHANGE_SERVICE_ACCOUNT", live_channel_id }),
    updateServiceAccountList: live_channels =>
      dispatch({ type: "UPDATE_SERVICE_ACCOUNT_LIST", live_channels }),
    proFeatureGate,
  })

  const handleConfigureStepFields = (type, step) => {
    let stepFields = step[`${type}_fields`]

    // On new mode (live_channel_id empty), choose first non-offline service
    if (!step.live_channel_id) {
      step.live_channel_id = step.service.live_channels.find(
        lc => !lc.offline
      )?.id
    }

    // DIY new Applets doesn't have stored fields and need to build the memory object in JS.
    // For all other cases, it is provided in the stored fields by Web::ConnectSDKFlow.inject_field_for_service_accounts.
    if (
      !stepFields.some(sf => sf.field_ui_type === "service_account_select") &&
      state.connectingService.allow_multiple_live_channels
    ) {
      stepFields.unshift({
        field_ui_type: "service_account_select",
        helper_text: "",
        hideable: false,
        label: `${state.connectingService.name} account`,
        name: "_live_channel_id",
        order: -1,
        normalized_field_type: "option",
        required: true,
      })
    }

    if (stepFields.length > 0) {
      return dispatch({
        type: `SELECTING_${type.toUpperCase()}_FIELDS`,
        value: step,
      })
    }

    trackSystemStepConfigSkipped({
      serviceModuleName: state.connectingService.module_name,
      stepType: type,
    })

    return dispatch({
      type: state.addingStep
        ? `${type.toUpperCase()}_ADDED`
        : `${type.toUpperCase()}_REPLACED`,
      value: { step, fields: [] },
    })
  }

  const handleStepSelect = (type, step) => {
    if (!isUserConnected(state.connectingService)) {
      return dispatch({
        type: `CONNECTING_${type.toUpperCase()}_SERVICE`,
        value: step,
      })
    }

    trackSystemServiceConnectionSkipped({
      serviceModuleName: state.connectingService.module_name,
      stepType: type,
    })

    handleConfigureStepFields(type, step)
  }

  const handleServiceConnection = (type, service) => {
    trackStateChangeServiceConnected({
      serviceModuleName: service.module_name,
      stepType: type,
    })

    dispatch({
      type: `${type.toUpperCase()}_SERVICE_CONNECTED`,
      live_channels: service.live_channels,
    })

    handleConfigureStepFields(type, {
      ...state.selectedStep,
      service: {
        ...state.selectedStep.service,
        live_channels: service.live_channels,
      },
    })
  }

  let currentStep
  switch (state.step) {
    case steps.INITIAL:
      currentStep = (
        <>
          {!state.editing && (
            <AppletLimitWarning user={user} className="initial-step" />
          )}
          <InitialStep
            editing={state.editing}
            sourceAppletId={state.editing ? importedState.id : null}
            userOwned={userOwned}
            userAtQuota={userAtQuota}
            dispatch={dispatch}
            state={state}
            urls={urls}
            user={user}
            makeItYourOwnFeaturePrompt={makeItYourOwnFeaturePrompt}
            makingItTheirOwn={state.makingItTheirOwn}
            proFeatureGate={proFeatureGate}
            onCreationSuccess={() => {
              // Remove warning about unsaved changes on redirect.
              cleanupBeforeUnload()
            }}
          />
        </>
      )
      break
    case steps.SELECTING_TRIGGER_SERVICE:
      currentStep = (
        <ServiceSelector
          loading={state.loading || isEmpty(state.services)}
          updateLoading={updateLoading}
          services={state.services.servicesWithTriggers}
          onServiceSelectQuery={DIYLoadServiceTriggersAndFieldsQuery}
          onServiceSelect={updatedService =>
            dispatch({
              type: "SELECTING_TRIGGER",
              value: updatedService,
            })
          }
          stepType={"trigger"}
        />
      )
      break
    case steps.SELECTING_ACTION_SERVICE:
      currentStep = (
        <ServiceSelector
          loading={state.loading || isEmpty(state.services)}
          updateLoading={updateLoading}
          services={state.services.servicesWithActions}
          onServiceSelectQuery={DIYLoadServiceActionsAndFieldsQuery}
          onServiceSelect={updatedService =>
            dispatch({
              type: "SELECTING_ACTION",
              value: updatedService,
            })
          }
          stepType={"action"}
        />
      )
      break
    case steps.SELECTING_QUERY_SERVICE:
      currentStep = (
        <ServiceSelector
          loading={state.loading || isEmpty(state.services)}
          updateLoading={updateLoading}
          services={state.services.servicesWithQueries}
          onServiceSelectQuery={DIYLoadServiceQueriesAndFieldsQuery}
          onServiceSelect={updatedService =>
            dispatch({
              type: "SELECTING_QUERY",
              value: updatedService,
            })
          }
          stepType={"query"}
        />
      )
      break
    case steps.CONNECTING_TRIGGER_SERVICE:
      currentStep = (
        <ServiceConnection
          service={state.connectingService}
          connectionFinishedUrl={urls.connectionFinishedUrl}
          onServiceConnected={service => {
            handleServiceConnection("trigger", service)
          }}
        />
      )
      break
    case steps.CONNECTING_ACTION_SERVICE:
      currentStep = (
        <ServiceConnection
          service={state.connectingService}
          connectionFinishedUrl={urls.connectionFinishedUrl}
          onServiceConnected={service => {
            handleServiceConnection("action", service)
          }}
        />
      )
      break
    case steps.CONNECTING_QUERY_SERVICE:
      currentStep = (
        <ServiceConnection
          service={state.connectingService}
          connectionFinishedUrl={urls.connectionFinishedUrl}
          onServiceConnected={service => {
            handleServiceConnection("query", service)
          }}
        />
      )
      break
    case steps.SELECTING_TRIGGER:
      currentStep = (
        <StepSelector
          onStepSelect={step => {
            handleStepSelect("trigger", step)
          }}
          selectedSteps={[]}
          steps={state.connectingService.public_triggers}
          stepType="trigger"
          service={state.connectingService}
          proFeatureGate={proFeatureGate}
        />
      )
      break
    case steps.SELECTING_ACTION:
      currentStep = (
        <StepSelector
          onStepSelect={step => {
            handleStepSelect("action", step)
          }}
          selectedSteps={
            msa
              ? selectedActionsAboveLimit(state.selectedActions)
              : state.selectedActions
          }
          steps={state.connectingService.public_actions}
          stepType="action"
          service={state.connectingService}
        />
      )
      break
    case steps.SELECTING_QUERY:
      currentStep = (
        <StepSelector
          onStepSelect={step => {
            handleStepSelect("query", step)
          }}
          selectedSteps={state.selectedQueries}
          steps={state.connectingService.public_queries}
          stepType="query"
          service={state.connectingService}
        />
      )
      break
    case steps.SELECTING_TRIGGER_FIELDS:
      currentStep = (
        <StepFieldsSelector
          {...selectingTandaFieldsProps()}
          onCreate={(step, fields) => {
            dispatch({
              type: state.addingStep ? "TRIGGER_ADDED" : "TRIGGER_REPLACED",
              value: { step, fields },
            })
          }}
          stage="this"
          stepFields={state.selectedStep.trigger_fields}
          stepType="trigger"
          proFeatureGate={proFeatureGate}
        />
      )
      break
    case steps.SELECTING_ACTION_FIELDS:
      currentStep = (
        <ErrorBoundary
          placeholder={
            <h3>
              Sorry, there was a problem getting these action details; we are
              investigating!
            </h3>
          }
        >
          <StepFieldsSelector
            {...selectingTandaFieldsProps()}
            onCreate={(step, fields) => {
              dispatch({
                type: state.addingStep ? "ACTION_ADDED" : "ACTION_REPLACED",
                value: { step, fields },
              })
            }}
            stage="that"
            stepFields={state.selectedStep.action_fields}
            stepType="action"
            ingredientsMetadata={ingredientsMetadataFromState(state)}
            trigger={state.selectedTrigger}
            queries={state.selectedQueries}
            defaultValueLoader={() => {
              return loadActionDefaultValues({
                trigger_module_name: state.selectedTrigger?.full_module_name,
                actionService:
                  state.selectedStep?.service || state.connectingService,
              })
            }}
          />
        </ErrorBoundary>
      )
      break
    case steps.SELECTING_QUERY_FIELDS:
      currentStep = (
        <StepFieldsSelector
          {...selectingTandaFieldsProps()}
          onCreate={(step, fields) => {
            dispatch({
              type: state.addingStep ? "QUERY_ADDED" : "QUERY_REPLACED",
              value: { step, fields },
            })
          }}
          stage="this"
          stepFields={state.selectedStep.query_fields}
          stepType="query"
          ingredientsMetadata={ingredientsMetadataFromState(state, false)}
          trigger={state.selectedTrigger}
        />
      )
      break
    case steps.ADDING_FILTER_CODE:
    case steps.EDITING_FILTER_CODE:
      currentStep = (
        <FilterCode
          contextTemplate={filterCodeTemplate}
          selectedTrigger={state.selectedTrigger}
          selectedQueries={state.selectedQueries}
          selectedActions={state.selectedActions}
          filterCode={state.filterCode}
          updateFilterCode={filterCode =>
            dispatch({ type: "FILTER_CODE_MODIFIED", value: filterCode })
          }
        />
      )
      break
    case steps.PREVIEW:
      currentStep = (
        <Preview
          actions={state.selectedActions}
          errors={state.errors}
          onCreationError={errors =>
            dispatch({
              type: "STEP_CREATION_FAILED",
              value: errors,
            })
          }
          onCreationSuccess={() => {
            // Remove warning about unsaved changes on redirect.
            cleanupBeforeUnload()
            // Avoid use Turbolinks.visit because pushState update state but browser doesn't have page cached.
            window.history.pushState({}, "My Applets - IFTTT", "/")
          }}
          preview={state.preview}
          queries={state.selectedQueries}
          trigger={state.selectedTrigger}
          filterCode={state.filterCode}
          userLogin={user.login}
        />
      )
  }

  const stepsToShowBgColor = [
    steps.CONNECTING_TRIGGER_SERVICE,
    steps.SELECTING_TRIGGER,
    steps.SELECTING_TRIGGER_FIELDS,

    steps.CONNECTING_QUERY_SERVICE,
    steps.SELECTING_QUERY,
    steps.SELECTING_QUERY_FIELDS,

    steps.CONNECTING_ACTION_SERVICE,
    steps.SELECTING_ACTION,
    steps.SELECTING_ACTION_FIELDS,
  ]

  const contextColor = () => {
    const bGColorService =
      state.selectedStep?.service || state.connectingService
    if (stepsToShowBgColor.includes(state.step) && bGColorService) {
      return bGColorService.brand_color
    } else if (stepsToShowBgColor.includes(state.step)) {
      return colors.primaryTextColor // Fallback in case any issue in flow with service
    } else if (state.step === steps.PREVIEW) {
      return Object.entries(state.preview).length
        ? state.preview.brandColor
        : "white"
    } else {
      return "white"
    }
  }

  const appletFeatureFlags = {
    pro_features: importedState?.normalized_applet?.pro_features,
    intermediate_pro_features:
      importedState?.normalized_applet?.intermediate_pro_features,
  }

  return (
    <BackgroundColorContext.Provider value={contextColor()}>
      <MakeItYourOwnModal
        visible={makeItYourOwn.showModal}
        onConvert={makeItYourOwn.onConvert}
        onCancel={makeItYourOwn.onCancel}
        onClose={() => {
          if (makeItYourOwn.onClose) {
            makeItYourOwn.onClose()
          }
          updateMakeItYourOwn({
            showModal: false,
          })
        }}
      />
      <UpgradeToProModal
        visible={upgradeToPro.showModal}
        step={upgradeToPro.step}
        newProSubscriptionUrl={urls.newProSubscriptionUrl}
        newProPlusSubscriptionUrl={urls.newProPlusSubscriptionUrl}
        onUpgrade={() => {
          dispatch({ type: "SUCCESSFUL_INLINE_UPGRADE" })
        }}
        onClose={() =>
          updateUpgradeToPro({
            showModal: false,
            step: "queries",
          })
        }
        appletsInUse={user.appletQuota.total - user.appletQuota.remaining}
        appletQuotaTotal={user.appletQuota.total}
        eligibleForTrial={user.eligibleForTrial}
        currentTier={user.tier}
        subscriptionPaymentType={user.subscriptionPaymentType}
        proPlusProductId={proPlusProductId}
        appletFeatureFlags={appletFeatureFlags}
        isTrialing={user.isTrialing}
        trackLocationType="diy_composer"
      />
      <Header
        editing={state.editing}
        user={user}
        updateLoading={updateLoading}
        title={stepsTitle(state)}
        step={state.step}
        urls={urls}
        backButtonTarget={goBack}
        showUpgradeModal={() =>
          updateUpgradeToPro({
            showModal: true,
            step: "default",
          })
        }
      />

      {state.step === steps.INITIAL && state.editing && userOwned ? (
        publishedByCurrentUser ? (
          <h6 className="published-applet-cta">
            Published on {publishedAppletData.publishedAt}, enabled by{" "}
            {publishedAppletData.enabledCount}{" "}
            {publishedAppletData.enabledCount === 1 ? "person" : "people"}{" "}
            <a
              className="link-inline open-in-new"
              href={urls.publishedAppletEditPath}
              target="_blank"
              rel="noreferrer"
            >
              Edit Published Applet
            </a>
          </h6>
        ) : (
          <h6 className="published-applet-cta">
            Want to publish this Applet so anyone can use it?{" "}
            <a
              style={{ textDecoration: "underline" }}
              className="link-inline open-in-new"
              href={urls.publishedAppletEditPath}
              target="_blank"
              rel="noreferrer"
            >
              Click here
            </a>
          </h6>
        )
      ) : null}

      <section styleName="diy-composer">{currentStep}</section>
    </BackgroundColorContext.Provider>
  )
}

const publicTQAShape = PropTypes.shape({
  id: PropTypes.string,
  module_name: PropTypes.string.isRequired,
  full_module_name: PropTypes.string.isRequired,
  normalized_module_name: PropTypes.string,
  name: PropTypes.string,
  description: PropTypes.string,
  weight: PropTypes.string,
})

DIY.propTypes = {
  importedState: PropTypes.object,
  filterCodeTemplate: PropTypes.string.isRequired,
  preloadedServices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      module_name: PropTypes.string.isRequired,
      description_html: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      brand_color: PropTypes.string.isRequired,
      incompatible_channels: PropTypes.array.isRequired,
      can_be_autoactivated: PropTypes.bool.isRequired,
      public_triggers: PropTypes.arrayOf(publicTQAShape).isRequired,
      public_actions: PropTypes.arrayOf(publicTQAShape).isRequired,
      public_queries: PropTypes.arrayOf(publicTQAShape).isRequired,
    })
  ).isRequired,
  user: PropTypes.shape({
    isTrialing: PropTypes.bool.isRequired,
    login: PropTypes.string.isRequired,
    eligibleForTrial: PropTypes.bool.isRequired,
    tier: PropTypes.string.isRequired,
    subscriptionPaymentType: PropTypes.string,
    appletQuota: PropTypes.shape({
      total: PropTypes.number.isRequired,
      remaining: PropTypes.number.isRequired,
    }).isRequired,
    permissions: PropTypes.objectOf(
      PropTypes.shape({
        permitted: PropTypes.bool.isRequired,
        minimumTier: PropTypes.string,
      })
    ),
  }).isRequired,
  urls: PropTypes.shape({
    connectionFinishedUrl: PropTypes.string.isRequired,
    newProPlusSubscriptionUrl: PropTypes.string.isRequired,
    newProSubscriptionUrl: PropTypes.string.isRequired,
    publishedAppletEditPath: PropTypes.string,
    connectionUrl: PropTypes.string, // required when editing/importedState
  }).isRequired,
  msa: PropTypes.bool,
  serviceSuggestion: PropTypes.string,
  personalAppletsCount: PropTypes.number.isRequired,
  proPlusProductId: PropTypes.string.isRequired,
}

DIY.defaultProps = {
  msa: false,
}

export default DIY
